import {observer} from 'mobx-react-lite'
import React, {useMemo} from 'react'
import {Lang} from 'yougile-lang'
import {LinkButtonRow} from '@components/common/sandbox/list-view/components/link-button-row'
import {resolveRowValue} from '../helpers'
import {useListStore} from '../list-store'
import * as Tw from '../tw-components'
import {
  AddMode,
  ColConfig,
  Identifiable,
  NullableId,
  RowConfig,
  RowContent
} from '../types'
import {AddItemRow} from './add-item-row'
import {Cell} from './cell'
import {FirstCell} from './first-cell'
import {ListRow} from './list-row'

const defaultRowRenderer = <TData extends Identifiable>(
  content: RowContent,
  _: RowConfig<TData>,
  __: ColConfig<TData>[]
) => content

enum LimitationStatusType {
  /**
   * List has not too many items to be limited
   */
  NoRequiredLimitation,
  /**
   * List is limited, because it has more than 5 done items
   */
  Limited,
  /**
   * List is unlimited, because it is added to the unlimited items list (see `ListStore.unlimitedItems`)
   */
  Unlimited
}

interface LimitationStatus {
  status: LimitationStatusType
  hiddenDoneItemsCount: number
}

interface RowsProps<TData extends object> {
  parentId: NullableId
  data: RowConfig<TData>[]
  columns: ColConfig<TData>[]
  nestedLevel: number
}

const useUndoneTaskLimitation = true

export const Rows = observer(
  <TData extends Identifiable>(props: RowsProps<TData>) => {
    const {parentId = null, data, columns, nestedLevel} = props
    /**
     * Contains the index of the first done item in the list or -1 if there is no done item
     * It uses for rendering the "Add item" row before the first done item
     */
    const firstDoneItemIndexRef = React.useRef<number>(-1)
    const store = useListStore()
    const {addItemState} = store
    const {rowRenderer = defaultRowRenderer, allowCreateItems} = store.config

    const [gridTemplateColumns, nestingMargin] = useMemo(
      () => store.createGridTemplate(columns, nestedLevel),
      [columns, nestedLevel]
    )

    /**
     * Represents the hierarchy id of the current list
     * Root has no parent, so it has ':root:' id, because it is still need to be identified
     */
    const hierarchyId = parentId === null ? ':root:' : parentId

    /**
     * Filter data
     */
    const {filteredData, limitationState} = useMemo(() => {
      let doneCount = 0
      let undoneCount = 0
      const limitationState: LimitationStatus = {
        status: LimitationStatusType.NoRequiredLimitation,
        hiddenDoneItemsCount: 0
      }
      firstDoneItemIndexRef.current = -1
      const filteredData = data.reduce<RowConfig<TData>[]>((acc, row) => {
        const isDone = store.isDone(row)
        if (isDone) {
          if (doneCount === 0) {
            firstDoneItemIndexRef.current = acc.length
          }
          doneCount++
        } else {
          undoneCount++
        }
        const showMoreVisible = useUndoneTaskLimitation
          ? doneCount + undoneCount > 15
          : isDone && doneCount > 10
        if (showMoreVisible) {
          limitationState.hiddenDoneItemsCount++
          if (!store.unlimitedItems.has(hierarchyId)) {
            limitationState.status = LimitationStatusType.Limited
          } else {
            limitationState.status = LimitationStatusType.Unlimited
            // Allow to add, because this item is unlimited
            acc.push(row)
          }
          // Skip done items after the first 5
          return acc
        }
        acc.push(row)
        return acc
      }, [])

      return {
        filteredData,
        limitationState
      }
    }, [props.data, store.unlimitedItems.has(hierarchyId)])

    /**
     * Contains the id of the first done item in the list or null if there is no done item (or no items at all)
     */
    return (
      <>
        {addItemState.enabled &&
          addItemState.id === parentId &&
          addItemState.mode === AddMode.BeforeUndoneTasks && (
            <AddItemRow
              isEditing={true}
              parentId={parentId}
              mode={AddMode.BeforeUndoneTasks}
              marginLeft={nestingMargin}
              showCreateButton={data.length !== 0}
            />
          )}
        {filteredData.map((row, i) => {
          let prependElement: React.ReactNode | null = null
          if (
            store.canCreateItemsFor(parentId) &&
            allowCreateItems &&
            i === firstDoneItemIndexRef.current
          ) {
            prependElement = (
              <AddItemRow
                isEditing={
                  addItemState.enabled &&
                  addItemState.id === parentId &&
                  addItemState.mode === AddMode.AfterUndoneTasks
                }
                parentId={parentId}
                mode={AddMode.AfterUndoneTasks}
                marginLeft={nestingMargin}
                showCreateButton={data.length !== 0}
                key={`add-item-before-${parentId}`}
              />
            )
          }

          return (
            <div key={row.data.id}>
              {prependElement}
              <Tw.TwRowContainer>
                {rowRenderer(
                  <ListRow
                    id={row.data.id}
                    rowConfig={row}
                    neighbours={data}
                    draggable={store.canDrag(row, parentId, nestedLevel)}
                    transferData={row.transferData}
                    nestedLevel={nestedLevel}
                    style={{
                      gridTemplateColumns,
                      marginLeft: nestingMargin
                    }}
                    onClick={event => store.onItemClick(row, event)}
                  >
                    <FirstCell
                      row={row}
                      nestedLevel={nestedLevel}
                      parentId={parentId}
                      isActive={store.getIsActive(row.data.id)}
                      isHighlighted={store.getIsHighlighted(row.data.id)}
                      childrenRows={row.children}
                    />
                    {columns.map((col, index) => {
                      return (
                        <Cell key={col.id || index} colConfig={col}>
                          {resolveRowValue(row, col)}
                        </Cell>
                      )
                    })}
                  </ListRow>,
                  row,
                  columns
                )}
                <Tw.TwBody>
                  {store.isExpanded(row.data.id) && (
                    <Rows
                      parentId={row.data.id}
                      data={row.children || []}
                      columns={columns}
                      nestedLevel={nestedLevel + 1}
                    />
                  )}
                </Tw.TwBody>
              </Tw.TwRowContainer>
            </div>
          )
        })}
        {limitationState.status === LimitationStatusType.Limited && (
          <LinkButtonRow
            onClick={() => store.addUnlimitedItem(hierarchyId)}
            nestingMargin={nestingMargin}
          >
            {Lang.msg.myTasks.expandDoneTasks} (
            {limitationState.hiddenDoneItemsCount} {Lang.msg.pcs})
          </LinkButtonRow>
        )}
        {limitationState.status === LimitationStatusType.Unlimited && (
          <LinkButtonRow
            onClick={() => store.removeUnlimitedItem(hierarchyId)}
            nestingMargin={nestingMargin}
          >
            {Lang.msg.myTasks.collapseDoneTasks}
          </LinkButtonRow>
        )}
        {(!useUndoneTaskLimitation ||
          limitationState.status ===
            LimitationStatusType.NoRequiredLimitation) &&
          store.canCreateItemsFor(parentId) &&
          allowCreateItems &&
          (data.length === 0 || firstDoneItemIndexRef.current === -1) && (
            <AddItemRow
              isEditing={
                addItemState.id === parentId &&
                addItemState.mode === AddMode.AfterUndoneTasks &&
                addItemState.enabled
              }
              parentId={parentId}
              mode={AddMode.AfterUndoneTasks}
              marginLeft={nestingMargin}
              showCreateButton={nestedLevel === 0 || data.length !== 0}
            />
          )}
      </>
    )
  }
)
